import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import { getLevelById } from '../../constants/accountLevels';

const AccountLevelConfirmation = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // O envio devolve se a conta subiu na hora ou ficou em análise, e é isso que muda o texto e o
  // próximo passo aqui. Sem esse estado (ex.: página recarregada direto nesta URL) a tela cai no
  // texto de "em análise", que é o caminho seguro quando não se sabe o desfecho real.
  const aprovadoNaHora = location.state?.status === 'approved';
  const mensagemDoEnvio = location.state?.message;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const levelId = useMemo(() => {
    const parsed = Number(level);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [level]);

  const levelConfig = useMemo(() => {
    if (!Number.isFinite(levelId)) return null;
    return getLevelById(levelId);
  }, [levelId]);

  if (!levelConfig) {
    return (
      <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-2xl font-semibold">Nível não encontrado</h1>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full bg-tBlue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
            onClick={() => navigate('/account/level')}
          >
            Voltar para níveis
            <i className="bi-arrow-left-short text-lg"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />

          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account/level')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <div className="mx-auto max-w-4xl">
            <div
              className={`mb-6 rounded-2xl p-6 ${aprovadoNaHora ? 'bg-emerald-50' : 'bg-yellow-50'}`}
            >
              <h1 className="mb-2 text-2xl font-bold text-slate-900">{levelConfig.title}</h1>
              <p className={`text-sm ${aprovadoNaHora ? 'text-emerald-700' : 'text-orange-700'}`}>
                {aprovadoNaHora
                  ? mensagemDoEnvio || 'Conta verificada. Você já está neste nível.'
                  : 'Nossa equipe irá validar suas informações.'}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-center">
                <div
                  className={`flex h-20 w-20 items-center justify-center rounded-full ${aprovadoNaHora ? 'bg-emerald-100' : 'bg-green-100'}`}
                >
                  <i
                    className={`bi-check-circle text-4xl ${aprovadoNaHora ? 'text-emerald-600' : 'text-green-600'}`}
                  ></i>
                </div>
              </div>

              <div className="mb-6 text-center">
                <h2 className="mb-3 text-xl font-semibold text-slate-900">
                  {aprovadoNaHora ? `${levelConfig.title} liberado!` : 'Dados enviados com sucesso!'}
                </h2>
                <p className="text-slate-600">
                  {aprovadoNaHora ? (
                    <>
                      Seus dados conferiram com o que já tínhamos, então a verificação foi concluída na
                      hora.
                      <br />
                      O {levelConfig.title.toLowerCase()} já está valendo para a sua conta.
                    </>
                  ) : (
                    <>
                      Suas informações foram recebidas e serão validadas pela nossa equipe.
                      <br />
                      O {levelConfig.title.toLowerCase()} será liberado em breve.
                    </>
                  )}
                </p>
              </div>

              {!aprovadoNaHora && (
                <div className="mb-6 rounded-lg bg-slate-50 p-4">
                  <p className="text-sm text-slate-600">
                    <i className="bi-info-circle mr-2 text-tBlue"></i>
                    Você receberá uma notificação assim que a validação for concluída.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => navigate(`/account/level/${levelId}`)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Ver detalhes do nível
                  <i className="bi-arrow-right-short text-lg"></i>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/account/level')}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-tBlue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
                >
                  Voltar para níveis
                  <i className="bi-arrow-left-short text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountLevelConfirmation;

